import { Injectable, inject } from '@angular/core';
import { CloudLogsService, LogType } from './cloud-logs.service';
import { ProcessLog } from 'src/app/user-identification/models/user-identification';

type TrackEndedHandler = () => void;

interface WatchSession {
	trackEndedHandlers: Map<MediaStreamTrack, TrackEndedHandler>;
	cameraPermissionStatus: PermissionStatus | null;
	cameraPermissionChangeHandler: (() => void) | null;
	revokedEmitted: boolean;
	onRevoked: () => void;
}

/**
 * Detecta revocación de permisos de cámara durante un paso activo (sin polling).
 * Escucha `MediaStreamTrack.onended` y, si existe, `navigator.permissions.query('camera').onchange`.
 */
@Injectable({
	providedIn: 'root',
})
export class CameraPermissionMonitorService {
	private readonly cloudLogsService = inject(CloudLogsService);

	/**
	 * Vigila un stream concedido. Devuelve función de limpieza (llamar en destroy o al cambiar stream).
	 */
	public watchStream(stream: MediaStream | null | undefined, onRevoked: () => void): () => void {
		const session: WatchSession = {
			trackEndedHandlers: new Map(),
			cameraPermissionStatus: null,
			cameraPermissionChangeHandler: null,
			revokedEmitted: false,
			onRevoked,
		};

		if (!stream) {
			return () => this.clearSession(session);
		}

		const tracks = stream.getVideoTracks?.() ?? [];
		for (const track of tracks) {
			if (track.readyState === 'ended') {
				this.emitRevoked(session, 'trackAlreadyEnded', { trackLabel: track.label });
				continue;
			}
			const handler: TrackEndedHandler = () =>
				this.emitRevoked(session, 'trackEnded', { trackLabel: track.label });
			session.trackEndedHandlers.set(track, handler);
			track.addEventListener('ended', handler);
		}
		this.subscribeToCameraPermissionChange(session);

		return () => this.clearSession(session);
	}

	private emitRevoked(session: WatchSession, source: string, extra?: Record<string, unknown>): void {
		if (session.revokedEmitted) {
			return;
		}
		session.revokedEmitted = true;
		this.cloudLogsService.sendCloudLogs(
			{ cameraPermissionRevoked: { source, ...extra } },
			ProcessLog.IN_PROCESS,
			LogType.Warning,
		);
		session.onRevoked();
	}

	private subscribeToCameraPermissionChange(session: WatchSession): void {
		if (!navigator.permissions?.query) {
			return;
		}
		navigator.permissions
			.query({ name: 'camera' as PermissionName })
			.then((status: PermissionStatus) => {
				if (session.revokedEmitted || session.trackEndedHandlers.size === 0) {
					return;
				}
				// Con stream activo, `prompt` suele ser desfase de la API (p. ej. tras conceder en ajustes);
				// no revocar: confiar en `track.onended` y solo reaccionar a `denied` explícito.
				if (status.state === 'denied') {
					this.emitRevoked(session, 'permissionsApi', { state: status.state });
					return;
				}
				if (status.state !== 'granted') {
					return;
				}
				session.cameraPermissionStatus = status;
				session.cameraPermissionChangeHandler = () => {
					if (status.state === 'denied') {
						this.emitRevoked(session, 'permissionsApiChange', { state: status.state });
					}
				};
				status.addEventListener('change', session.cameraPermissionChangeHandler);
			})
			.catch(() => undefined);
	}

	private clearSession(session: WatchSession): void {
		session.trackEndedHandlers.forEach((handler: TrackEndedHandler, track: MediaStreamTrack) => {
			track.removeEventListener('ended', handler);
		});
		session.trackEndedHandlers.clear();
		if (session.cameraPermissionStatus && session.cameraPermissionChangeHandler) {
			session.cameraPermissionStatus.removeEventListener('change', session.cameraPermissionChangeHandler);
		}
		session.cameraPermissionStatus = null;
		session.cameraPermissionChangeHandler = null;
	}
}
